import { useQueryClient } from '@tanstack/react-query';
import {
  getApiV1PersonIdAddressesQueryOptions,
  useDeleteApiV1PersonIdAddressesAddresspersonid,
  usePostApiV1PersonIdAddresses,
  usePostApiV1PersonIdAddressesCopyToPayment,
  usePutApiV1PersonIdAddressesAddresspersonid,
} from '@/shared/api/generated';
import type {
  CreatePersonAddressRequest,
  UpdatePersonAddressRequest,
} from '@/shared/api/generated/types';

/**
 * Hook para gerenciar endereços de uma pessoa via API.
 *
 * Encapsula os endpoints POST, PUT, DELETE e copy-to-payment de endereços.
 * Invalida o cache de endereços após cada operação bem-sucedida.
 *
 * @param personId - ID da pessoa dona dos endereços
 */
export function usePersonAddresses(personId: number) {
  const queryClient = useQueryClient();

  const invalidate = () =>
    void queryClient.invalidateQueries(getApiV1PersonIdAddressesQueryOptions(personId));

  const createAddress = usePostApiV1PersonIdAddresses({
    mutation: { onSuccess: invalidate },
  });

  const updateAddress = usePutApiV1PersonIdAddressesAddresspersonid({
    mutation: { onSuccess: invalidate },
  });

  const deleteAddress = useDeleteApiV1PersonIdAddressesAddresspersonid({
    mutation: { onSuccess: invalidate },
  });

  const copyToPayment = usePostApiV1PersonIdAddressesCopyToPayment({
    mutation: { onSuccess: invalidate },
  });

  return {
    createAddress: (data: CreatePersonAddressRequest) =>
      createAddress.mutateAsync({ id: personId, data }),

    updateAddress: (addressPersonId: number, data: UpdatePersonAddressRequest) =>
      updateAddress.mutateAsync({ id: personId, addressPersonId, data }),

    deleteAddress: (addressPersonId: number) =>
      deleteAddress.mutateAsync({ id: personId, addressPersonId }),

    copyPersonalToPayment: () => copyToPayment.mutateAsync({ id: personId }),

    isPending:
      createAddress.isPending ||
      updateAddress.isPending ||
      deleteAddress.isPending ||
      copyToPayment.isPending,

    isCopyingToPayment: copyToPayment.isPending,
  } as const;
}
